import axios from 'axios';
import config from '../config';

const PENDING_FAVOURITES_STORAGE_KEY = 'pendingFavourites';

function readPendingFavourites() {
  try {
    const raw = window.localStorage.getItem(PENDING_FAVOURITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writePendingFavourites(list) {
  try {
    window.localStorage.setItem(PENDING_FAVOURITES_STORAGE_KEY, JSON.stringify(Array.isArray(list) ? list : []));
  } catch (e) {
    // ignore storage failures
  }
}

function normalizeFavouriteUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const u = new URL(trimmed);
    u.hash = '';

    // Canonicalize query keys across upstream variants (id vs itemId, categoryId vs category/cid)
    const id = u.searchParams.get('id') || u.searchParams.get('itemId') || u.searchParams.get('item_id');
    const categoryId = u.searchParams.get('categoryId') || u.searchParams.get('category') || u.searchParams.get('cid');

    if (id) {
      u.searchParams.delete('id');
      u.searchParams.delete('itemId');
      u.searchParams.delete('item_id');
      u.searchParams.set('id', id);
    }
    if (categoryId) {
      u.searchParams.delete('categoryId');
      u.searchParams.delete('category');
      u.searchParams.delete('cid');
      u.searchParams.set('categoryId', categoryId);
    }

    return u.toString();
  } catch (e) {
    return trimmed;
  }
}

function getProductUrlFromProduct(product) {
  if (!product) return null;
  if (product.productLink) return normalizeFavouriteUrl(product.productLink);

  // Fallback for product detail where we have id/categoryId in the route/product
  const id = product.id || product.itemId;
  const categoryId = product.categoryId || 0;
  if (id) {
    return normalizeFavouriteUrl(`https://www.goofish.com/item?id=${encodeURIComponent(id)}&categoryId=${encodeURIComponent(categoryId)}`);
  }

  return null;
}

function getPendingFavourites() {
  return readPendingFavourites();
}

function clearPendingFavourites() {
  writePendingFavourites([]);
}

function setPendingFavourite(url) {
  const normalized = normalizeFavouriteUrl(url);
  if (!normalized) return false;

  const existing = readPendingFavourites();
  if (existing.includes(normalized)) return false;

  const next = [normalized, ...existing].slice(0, 50);
  writePendingFavourites(next);
  return true;
}

async function applyPendingFavourites() {
  const pending = readPendingFavourites();
  if (!pending.length) return { appliedCount: 0, remainingCount: 0 };

  let existingSet = null;
  try {
    const list = await listFavourites();
    existingSet = new Set((Array.isArray(list) ? list : []).map((f) => (f && f.url ? normalizeFavouriteUrl(f.url) : null)).filter(Boolean));
  } catch (err) {
    // If not logged in, keep pending as-is.
    if (err && err.response && err.response.status === 401) {
      return { appliedCount: 0, remainingCount: pending.length };
    }
    existingSet = null;
  }

  const remaining = [];
  let appliedCount = 0;

  for (const url of pending) {
    const normalized = normalizeFavouriteUrl(url);
    if (!normalized) continue;
    if (existingSet && existingSet.has(normalized)) {
      appliedCount += 1;
      continue;
    }

    try {
      await addFavourite(normalized);
      appliedCount += 1;
      if (existingSet) existingSet.add(normalized);
    } catch (err) {
      if (err && err.response && err.response.status === 401) {
        // Session expired mid-apply; keep this and the rest.
        remaining.push(normalized);
        continue;
      }
      // Any other error: keep it in pending so user can retry after refresh/login.
      remaining.push(normalized);
    }
  }

  writePendingFavourites(remaining);
  return { appliedCount, remainingCount: remaining.length };
}

async function listFavourites(options = {}) {
  const requestedPage = Number.parseInt(options?.page, 10);
  const requestedPageSize = Number.parseInt(options?.pageSize, 10);
  const paginated =
    !!options?.paginated ||
    Number.isFinite(requestedPage) ||
    Number.isFinite(requestedPageSize);

  const params = paginated
    ? {
        paginated: 1,
        page: Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1,
        pageSize: Number.isFinite(requestedPageSize) && requestedPageSize > 0 ? requestedPageSize : undefined,
      }
    : undefined;

  const resp = await axios.get(`${config.apiBaseUrl}/api/profile/favourites`, {
    withCredentials: true,
    params,
  });
  console.log('Favourites response:', resp);

  if (paginated) {
    const data = resp.data || {};
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      items,
      page: typeof data.page === 'number' ? data.page : (params?.page || 1),
      pageSize: typeof data.pageSize === 'number' ? data.pageSize : (params?.pageSize || items.length || 1),
      totalCount: typeof data.totalCount === 'number' ? data.totalCount : items.length,
      totalPages: typeof data.totalPages === 'number' ? data.totalPages : 1,
      hasNextPage: !!data.hasNextPage,
      hasPreviousPage: !!data.hasPreviousPage,
    };
  }

  return Array.isArray(resp.data) ? resp.data : [];
}

async function addFavourite(url) {
  const normalized = normalizeFavouriteUrl(url);
  if (!normalized) throw new Error('Invalid url');
  const resp = await axios.post(`${config.apiBaseUrl}/api/profile/favourites`, { url: normalized }, { withCredentials: true });
  return resp.data;
}

async function removeFavourite(url) {
  const normalized = normalizeFavouriteUrl(url);
  if (!normalized) throw new Error('Invalid url');
  const resp = await axios.delete(`${config.apiBaseUrl}/api/profile/favourites`, { data: { url: normalized }, withCredentials: true });
  return resp.data;
}

export default {
  normalizeFavouriteUrl,
  getProductUrlFromProduct,
  getPendingFavourites,
  setPendingFavourite,
  clearPendingFavourites,
  applyPendingFavourites,
  listFavourites,
  addFavourite,
  removeFavourite,
};
